import { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useChatMessages } from '@/hooks/useChatMessages';
import { useChat } from '@/hooks/useChat';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Send, X, MessageSquare, Loader2, Minus, Paperclip, Smile } from 'lucide-react';
import MessageBubble from './MessageBubble';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTypingIndicator } from '@/hooks/useTypingIndicator';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useIsMobile } from '@/hooks/use-mobile';

const COMMON_EMOJIS = ['😊', '😂', '❤️', '👍', '🎉', '🔥', '🤔', '😢', '😍', '🚀', '👋', '✅', '❌', '👀', '✨', '🙌', '😎', '🤢', '😡', '🙏'];


interface QuickChatPanelProps {
    targetUser: {
        id: string;
        name: string;
        avatarUrl?: string;
    };
    onClose: () => void;
    isOpen: boolean;
    index?: number;
}

export function QuickChatPanel({ targetUser, onClose, isOpen, index = 0 }: QuickChatPanelProps) {
    const { user: currentUser } = useAuth();
    const isMobile = useIsMobile();
    const { conversations, createConversation, sendMessage, isSending, markAsRead } = useChat();
    const [newMessage, setNewMessage] = useState('');

    // Initialize minimized state from localStorage
    const [isMinimized, setIsMinimized] = useState(() => {
        const saved = localStorage.getItem(`chat_minimized_${targetUser.id}`);
        return saved ? JSON.parse(saved) : false;
    });



    const [isCreatingConversation, setIsCreatingConversation] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const lastTypingSentRef = useRef(0);

    // Find existing direct conversation with this user
    const existingConversation = conversations?.find(conv =>
        conv.type === 'direct' &&
        conv.participants?.some(p => p.id === targetUser.id) &&
        conv.participants?.some(p => p.id === currentUser?.id)
    );

    // Save minimized state
    useEffect(() => {
        localStorage.setItem(`chat_minimized_${targetUser.id}`, JSON.stringify(isMinimized));

        // If maximized, mark as read
        if (!isMinimized && isOpen && existingConversation?.id) {
            markAsRead(existingConversation.id);
        }
    }, [isMinimized, targetUser.id, isOpen, existingConversation?.id, markAsRead]);

    // Typing Indicator
    const { isTyping, sendTypingEvent } = useTypingIndicator({
        conversationId: existingConversation?.id || null
    });

    // Base64 notification sound (subtle pop)
    const notificationSound = 'data:audio/mp3;base64,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';

    const { messages, isLoading: isLoadingMessages } = useChatMessages(existingConversation?.id || null);
    const prevMessagesLengthRef = useRef(0);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }

        // Play sound if new message arrived and it's not from current user
        if (messages.length > prevMessagesLengthRef.current) {
            const newestMessage = messages[0];
            if (newestMessage && newestMessage.sender_id !== currentUser?.id) {
                const audio = new Audio(notificationSound);
                audio.volume = 0.5;
                audio.play().catch(e => console.log('Audio play failed', e));

                // Also mark as read if panel is open and not minimized
                if (existingConversation?.id && !isMinimized && isOpen) {
                    markAsRead(existingConversation.id);
                }
            }
        }

        // Initial mark as read when opening or loading
        if (messages.length > 0 && existingConversation?.id && !isMinimized && isOpen) {
            // We can check if the last message is unread here, but calling it safe idempotent is fine
            // However, to avoid too many requests, we might want to check
            // For simplicity, relying on useMutation debouncing or just calling it is okay for now
            // But actually validation is better.
        }

        prevMessagesLengthRef.current = messages.length;
    }, [messages, currentUser, isMinimized, isOpen, existingConversation?.id, markAsRead]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewMessage(e.target.value);

        // Throttle typing event to once every 2 seconds
        const now = Date.now();
        if (now - lastTypingSentRef.current > 2000) {
            sendTypingEvent();
            lastTypingSentRef.current = now;
        }
    };

    const handleSend = async () => {
        if (!newMessage.trim()) return;

        // If no conversation exists, create one first
        if (!existingConversation) {
            setIsCreatingConversation(true);
            createConversation({
                participantIds: [targetUser.id],
                type: 'direct',
            }, {
                onSuccess: (newConv) => {
                    setIsCreatingConversation(false);
                    // Send the message to the new conversation
                    sendMessage({
                        conversationId: newConv.id,
                        content: newMessage,
                    });
                    setNewMessage('');
                },
                onError: () => {
                    setIsCreatingConversation(false);
                }
            });
        } else {
            sendMessage({
                conversationId: existingConversation.id,
                content: newMessage,
            }, {
                onSuccess: () => {
                    setNewMessage('');
                }
            });
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!isOpen) return null;

    const initials = targetUser.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase();

    // Dynamic classes for panel size and position
    const panelClasses = cn(
        "fixed z-[9999] bg-card border shadow-2xl transition-all duration-300 overflow-hidden",
        isMobile
            ? (isMinimized ? "bottom-0 w-full h-14 rounded-t-lg left-0 right-0" : "inset-0 w-full h-full rounded-none")
            : (isMinimized ? "bottom-4 w-72 h-14 rounded-lg" : "bottom-4 w-80 h-[480px] rounded-lg")
    );

    const panelStyle = isMobile ? {} : { right: `${16 + (index * 330)}px` };

    const chatPanel = (
        <div
            className={panelClasses}
            style={panelStyle}
        >
            {/* Header */}
            <div
                className="flex items-center justify-between p-3 bg-emerald-600 dark:bg-emerald-700 text-white cursor-pointer"
                onClick={() => setIsMinimized(!isMinimized)}
            >
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Avatar className="h-8 w-8 ring-2 ring-white/20">
                            {targetUser.avatarUrl && (
                                <AvatarImage src={targetUser.avatarUrl} alt={targetUser.name} />
                            )}
                            <AvatarFallback className="bg-white/20 text-white text-xs">
                                {initials}
                            </AvatarFallback>
                        </Avatar>
                        <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-300 ring-2 ring-emerald-600" />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm font-semibold leading-none">
                            {targetUser.name}
                        </span>
                        <div className="flex items-center gap-1">
                            <span className="text-[10px] text-white/80">Online agora</span>
                            {/* Typing Indicator in Header */}
                            {isTyping && (
                                <span className="text-[10px] text-white font-bold animate-pulse ml-1">
                                    • Digitando...
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-white hover:bg-white/20"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsMinimized(!isMinimized);
                        }}
                    >
                        <Minus className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-white hover:bg-white/20"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Chat Content - Hidden when minimized */}
            {!isMinimized && (
                <>
                    {/* Messages Area */}
                    <ScrollArea
                        ref={scrollRef}
                        className="flex-1 h-[360px] p-4 bg-muted/30 dark:bg-muted/10"
                    >
                        {isLoadingMessages || isCreatingConversation ? (
                            <div className="flex items-center justify-center h-full">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : messages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-center">
                                <MessageSquare className="h-10 w-10 mb-3 opacity-30" />
                                <p className="text-sm">Nenhuma mensagem ainda</p>
                                <p className="text-xs opacity-70 mt-1">Envie uma mensagem para {targetUser.name}</p>
                            </div>
                        ) : (
                            <div className="flex flex-col justify-end min-h-full space-y-2">
                                {isTyping && (
                                    <div className="text-xs text-muted-foreground ml-2 animate-pulse mb-1">
                                        {targetUser.name.split(' ')[0]} está digitando...
                                    </div>
                                )}
                                {[...messages].reverse().map((msg) => (
                                    <MessageBubble
                                        key={msg.id}
                                        message={msg}
                                        isCurrentUser={msg.sender_id === currentUser?.id}
                                    />
                                ))}
                            </div>
                        )}
                    </ScrollArea>

                    {/* Input Area */}
                    <div className="p-3 bg-background border-t gap-2 flex items-center">
                        <div className="flex gap-1 mr-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                                <Paperclip className="h-4 w-4" />
                            </Button>
                        </div>
                        <Input
                            className="flex-1 h-9 bg-muted/50 border-0 focus-visible:ring-1 focus-visible:ring-emerald-500"
                            placeholder="Digite sua mensagem..."
                            value={newMessage}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyPress}
                            disabled={isSending || isCreatingConversation}
                        />


                        <div className="flex gap-1 ml-1">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-muted-foreground hover:text-emerald-600"
                                    >
                                        <Smile className="h-4 w-4" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-64 p-2" side="top" align="end">
                                    <div className="grid grid-cols-5 gap-1">
                                        {COMMON_EMOJIS.map(emoji => (
                                            <button
                                                key={emoji}
                                                className="hover:bg-muted p-1 text-lg rounded-md transition-colors"
                                                onClick={() => setNewMessage(prev => prev + emoji)}
                                            >
                                                {emoji}
                                            </button>
                                        ))}
                                    </div>
                                </PopoverContent>
                            </Popover>
                            <Button
                                size="icon"
                                className="h-8 w-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={handleSend}
                                disabled={!newMessage.trim() || isSending || isCreatingConversation}
                            >
                                {isSending || isCreatingConversation ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Send className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );

    return createPortal(chatPanel, document.body);
}
